/*
 * scaleValue - A utility function to scale numeric values based on the unit provided.
 * The function supports scaling to kilo (k), mega (M), and giga (G) for a given unit
 * and also handles optional unit scaling.
 *
 
 * Scaling Logic:
 * - The function checks the value and determines whether to scale to kilo (k), mega (M), or giga (G) 
 *   based on its magnitude.
 * - If the value is between -1000 and 1000, it remains unscaled.
 * - If the value falls between 1k and 1M (or -1k and -1M), it is scaled to kilo (k).
 * - If the value falls between 1M and 1G (or -1M and -1G), it is scaled to mega (M).
 * - If the value exceeds 1G (or falls below -1G), it is scaled to giga (G).
 *

 * Example Usage:
 * const result = scaleValue(1200000, 'W'); // { scaledValue: 1.2, scale: 'MW' }
 * const result = scaleValue(500, 'VA');    // { scaledValue: 500, scale: 'VA' }
 * const result = scaleValue(1500000, null, 'kVA'); // { scaledValue: 1500, scale: 'kVA' }
 */

export function scaleValue(value, unit, optionalUnit) {
  
  // Define scaling factors for kilo, mega, and giga
  const kilo = 1000;
  const mega = kilo * 1000;
  const giga = mega * 1000;

  // Case 1: If a base unit is provided (e.g., 'W', 'VA'), determine the appropriate scale
  if (unit) {
    let scaledValue = value;
    let scale = '';

    if (value < kilo && value > -kilo) {
      // No scaling needed for values between -1000 and 1000
      scaledValue = value;
      scale = unit;
    } else if (
      (value >= kilo && value < mega) ||
      (value <= -kilo && value > -mega)
    ) {
      // Scale to kilo (k) for values between 1k and 1M, or -1k and -1M
      scaledValue = value / kilo;
      scale = `k${unit}`;
    } else if (
      (value >= mega && value < giga) ||
      (value <= -mega && value > -giga)
    ) {
      // Scale to mega (M) for values between 1M and 1G, or -1M and -1G
      scaledValue = value / mega;
      scale = `M${unit}`;
    } else {
      // Scale to giga (G) for values larger than 1G or smaller than -1G
      scaledValue = value / giga;
      scale = `G${unit}`;
    }

    // Return the scaled value, rounded to two decimal places, along with the scale

    // console.log(+scaledValue?.toFixed(2));
    return {
      // scaledValue: +scaledValue !== 'NaN' && +scaledValue?.toFixed(2),
      scaledValue: Number.isNaN(+scaledValue)
        ? null
        : Number((+scaledValue).toFixed(2)),
      scale,
    };

    // Case 2: If no base unit is provided but an optional unit is available, apply custom scaling
  } else if (optionalUnit) {
    let scaledValue = value;
    let scale = '';

    if (optionalUnit.includes('k')) {
      // Custom scaling for kilo
      scaledValue = value / 1000;
      scale = optionalUnit;
    } else if (optionalUnit.includes('M')) {
      // Custom scaling for mega
      scaledValue = value / 1000000;
      scale = optionalUnit;
    } else {
      // No scaling, use the optional unit directly
      scaledValue = value;
      scale = optionalUnit;
    }

    // Return the scaled value, rounded to two decimal places, along with the optional scale
    return {
      // scaledValue: +scaledValue !== 'NaN' && +scaledValue?.toFixed(2),
      scaledValue: !scaledValue || Number.isNaN(+scaledValue) ? null : Number(scaledValue.toFixed(2)),
      scale,
    };

    // Case 3: If no unit or optional unit is provided, return the original value with no scaling
  } else {
    return {
      // scaledValue: +value !== 'NaN' && Math.round(value * 100) / 100,
      scaledValue: value == null || Number.isNaN(+value) ? null : Math.round(value * 100) / 100,
      scale: unit,
    };
  }
}
